import { ChatBot, NextPage, NextPageHead, PageView, VWOExperimentPush } from '@aspendental/shared-components-web';
import { PageThemeContextProvider } from '@aspendental/shared-components-web/lib/context/PageThemeProvider.ctx';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { fetchAppData } from './utils/app-data-fetcher';
import { generateBreadcrumbs } from './utils/breadcrumb-generator';
import getConfigByBrand from './utils/get-config-by-brand';
import { getDeviceTypeFromHeaders } from './utils/get-device-type-from-headers';
import getHostFromHeaders from './utils/get-host-from-headers';
import getThemeByDomainName from './utils/get-theme-by-domain-name';
import { getUserCoordinates } from './utils/get-user-coordinates';
import { processPageContents } from './utils/page-contents-processor';
import { PATHFINDER_REFERRAL_PARAM, PATHFINDER_REFERRAL_VALUE, resolvePathfinderResultPage, setPathfinderReferralCookie } from './utils/pathfinder-routing';
import { CACHE_CONTROL_NO_CACHE, CACHE_CONTROL_SHORT_TTL } from '@src/constants';
import { PageByAppAndAlternatePathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import { resolveServerSideExperiments } from '@src/lib/vwo-fme/resolve-server-side-experiments';
import choosePreview from '@src/pages/utils/choose-preview';
import DetermineIndexing from '@src/pages/utils/determine-indexing';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const pageData = useContentfulLiveUpdates(props.page);
  const {
    url: logoUrl
  } = props.appData?.logo ?? {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = pageData;
  const pageTheme = props.page?.pageTheme || 'Primary';
  const {
    siteURL,
    features,
    featureFlags,
    logo
  } = props.config as BrandThemeConfig;
  const {
    preview,
    breadcrumbsData,
    pageReferrer,
    domain
  } = props;
  const router = useRouter();

  // Pathfinder ConvertFlow quiz completion → route to the appropriate result
  // page. The ConvertFlowQuiz component fires a `convertFlowQuizComplete`
  // window event with the accumulated quiz answers once cfCompletion fires.
  // We resolve the result page here (in the Next.js page layer) so we can use
  // router.push() instead of window.location, preserving SPA behaviour.
  useEffect(() => {
    const handleQuizComplete = (event: Event) => {
      const {
        quizAnswers
      } = (event as CustomEvent<{
        quizAnswers: Record<string, string>;
      }>).detail ?? {};
      if (!quizAnswers) return;

      // Always mark this visit as pathfinder regardless of quiz answers or
      // credit score — the cookie may already be set by ConvertFlowQuiz's
      // cfSubmit handler, but belt-and-suspenders here covers the case where
      // cfCompletion fires after CF's redirect.
      try {
        setPathfinderReferralCookie();
      } catch {
        // Cookie API unavailable
      }
      const resultPage = resolvePathfinderResultPage(quizAnswers);
      if (resultPage) {
        const resultUrl = `${resultPage}?${PATHFINDER_REFERRAL_PARAM}=${PATHFINDER_REFERRAL_VALUE}`;
        void router.push(resultUrl);
      }
    };
    window.addEventListener('convertFlowQuizComplete', handleQuizComplete);
    return () => window.removeEventListener('convertFlowQuizComplete', handleQuizComplete);
  }, [router]);

  // Belt-and-suspenders: on the result page itself, re-set the cookie from the
  // URL param in case the user bookmarked or refreshed the result page URL.
  useEffect(() => {
    const urlValue = new URLSearchParams(window.location.search).get(PATHFINDER_REFERRAL_PARAM);
    if (urlValue === PATHFINDER_REFERRAL_VALUE) {
      try {
        setPathfinderReferralCookie();
      } catch {
        // Cookie API unavailable
      }
    }
  }, []);
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="Page" data-sentry-source-file="[...slug].page.tsx">
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Marketing" data-sentry-element="PageView" data-sentry-source-file="[...slug].page.tsx" />
			<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={description} canonical={canonical} breadcrumbTitle={breadcrumbsData?.breadcrumbTitle} breadcrumbs={breadcrumbsData?.breadcrumbsCollection.items} originUrl={siteURL} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} contentsCollection={pageData.contentsCollection} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} data-sentry-element="VWOExperimentPush" data-sentry-source-file="[...slug].page.tsx" />
			<NextPage pageData={{
      ...pageData
    }} data-sentry-element="NextPage" data-sentry-source-file="[...slug].page.tsx" />
			{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</PageThemeContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  params,
  query,
  locale,
  req,
  res,
  preview = false
}) => {
  if (!params?.slug) {
    return {
      notFound: true
    };
  }
  const domain = getHostFromHeaders(req?.headers);
  const theme = getThemeByDomainName(domain);
  const headerTagTheme = Array.isArray(req.headers['x-tag-theme']) ? (req.headers['x-tag-theme'] as TagThemeType[])[0] : req.headers['x-tag-theme'] as TagThemeType;
  // Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
  const tagTheme = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
  const logger = getLogger('landing-page', tagTheme);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(domain, preview);
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const gqlClient = getClient(preview);
    if (gqlClient === null) {
      logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const appData = await fetchAppData(gqlClient, appName, preview, logger);
    if (!appData) {
      return {
        notFound: true
      };
    }
    const slugs = params.slug as string[];
    const path = '/' + slugs.join('/');
    const alternativePath = path.endsWith('/') ? path : path + '/';
    const pageData = await gqlClient.query(PageByAppAndAlternatePathDocument, {
      appName,
      path,
      alternativePath,
      preview
    }).toPromise();
    if (pageData.error) {
      logger.error(`CONTENTFUL_ERROR_SLUG_PAGE: PATH - ${path} - ERROR: ${pageData.error}`);
    }
    const page = pageData.data?.pageCollection?.items?.[0] as PageFragment | undefined;
    if (!page) {
      if (pageData.error) {
        // Contentful errored with no usable data — mark it uncacheable so the
        // CDN can't cache this blip and amplify it into a sustained probe failure.
        // Note: the error may be transient (network/429/5xx) or permanent (schema
        // mismatch, query complexity). We log as NO_DATA rather than TRANSIENT to
        // avoid misleading alert labels, and include the error for debugging.
        res.setHeader('Cache-Control', 'private, no-store');
        logger.error(`CONTENTFUL_ERROR_NO_DATA_SLUG_PAGE: PATH - ${path} - ERROR: ${pageData.error}`);
      } else {
        logger.error(`COULD_NOT_FIND_THE_PAGE_WITH_PATH: ${path}`);
      }
      return {
        notFound: true
      };
    }

    // Process page contents: fetch by ID, enrich with entry links
    const enrichedPageContents = await processPageContents(gqlClient, preview, logger, page?.contentsCollection?.items);

    // Resolve VWO FME feature flags for Server-Side ExperimentContainers
    const {
      decisions: experimentDecisions
    } = await resolveServerSideExperiments(enrichedPageContents, brandConfig, req, res, query, logger);

    // Resolve standalone VWO FME flags (layout-level header swap, etc.)
    const {
      flags: serverFeatureFlags,
      decisions: flagDecisions
    } = await resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, brandConfig, req, res, query, logger);
    const vwoDecisions = [...experimentDecisions, ...flagDecisions];

    // breadcrumb data generation from url path if not in page data
    const breadcrumbsData = await generateBreadcrumbs(gqlClient, appName, slugs, page, preview, logger);
    if (preview) {
      res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
    } else if (!res.getHeader('Cache-Control')) {
      // Skip if an earlier resolver already pinned the response to a
      // non-shareable Cache-Control (e.g. visitor-id mint sets
      // "private, no-store" to prevent CDN cookie poisoning).
      res.setHeader('Cache-Control', CACHE_CONTROL_SHORT_TTL);
    }
    const {
      coordinates,
      hasIpBasedCoordinates
    } = getUserCoordinates(req, logger);
    const transformedServices = getConfigServices(brandConfig);
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
          CC_DENTRINO_CLIENT_ID: process.env.CC_DENTRINO_CLIENT_ID ?? '',
          CC_DENTRINO_CLIENT_SECRET: process.env.CC_DENTRINO_CLIENT_SECRET ?? '',
          DENTRINO_CLIENT_ID: process.env.DENTRINO_CLIENT_ID,
          DENTRINO_CLIENT_SECRET: process.env.DENTRINO_CLIENT_SECRET ?? '',
          ZENOTI_API_KEY_LIV: process.env.ZENOTI_API_KEY_LIV,
          ZENOTI_CENTER_ID: process.env.ZENOTI_CENTER_ID,
          ZENOTI_CENTER_ID_IL: process.env.ZENOTI_CENTER_ID_IL
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: {
          ...page,
          contentsCollection: {
            items: enrichedPageContents
          }
        },
        user: {
          coordinates,
          hasIpBasedCoordinates
        },
        appData,
        footerData: appData?.footer,
        preview,
        breadcrumbsData,
        deviceType,
        pageReferrer,
        stateCodeFromHeaders,
        domain: domain ?? '',
        vwoDecisions,
        serverFeatureFlags,
        hideNavigation: (enrichedPageContents?.some(item => item?.__typename === 'Quiz' && (item as {
          quizType?: string;
        }).quizType === 'Pathfinder') ?? false) || ['/lp/meta-result-candidate', '/lp/meta-result-call', '/pathfinder-quiz'].includes(path.replace(/\/$/, ''))
      }
    };
  } catch (e) {
    // Override any previously-set cacheable header (e.g. CACHE_CONTROL_SHORT_TTL
    // set after a successful page fetch) so the CDN doesn't cache the error 404.
    res.setHeader('Cache-Control', 'private, no-store');
    logger.error(`LANDING_PAGE_LOAD_FAILURE: ${e}`);
    return {
      notFound: true
    };
  }
};
export default Page;